/**
 * @engine/physics2d — 2D Physics Engine
 *
 * Provides rigid-body dynamics, collision detection, raycasting,
 * and GPU-compute-ready struct definitions for a 2D game engine.
 *
 * @module @engine/physics2d
 */

// ── Core types & constants ──────────────────────────────────────────────────
export {
  BodyType,
  ColliderType,
  CapsuleDirection,
  ForceMode2D,
  CollisionDetection2D,
  SleepState,  CombineMode,
  GravityZoneType,
  GravityZoneShape,
  GravityFalloff,
  MAX_POLYGON_VERTICES,
  PhysicsLayers,
  SOLVER_DEFAULTS,
  defaultMaterial,
  packConstraints,
  unpackConstraints,
  validateConvexPolygon,
  computePolygonNormals,
  computeColliderAABB,
  type PhysVec2,
  type MassData,
  type PhysicsMaterial2D,
  type RigidBodyConstraints,
  type ContactPoint2D,
  type CollisionManifold,
  type RaycastHit2D,
  type OverlapResult2D,
  type CollisionEvent2D,
  type TriggerEvent2D,
  type PhysicsBodyState,
  type ColliderState,
  type PhysicsWorldConfig,
  type GravityZoneConfig,
} from './types';

// ── Collision shapes ────────────────────────────────────────────────────────
export {
  CircleShape,
  BoxShape,
  CapsuleShape,
  PolygonShape,
  EdgeShape,
  extractTilemapEdges,
  type Shape2D,
} from './shapes/Shape2D';

// ── Convex hull utilities ───────────────────────────────────────────────────
export {
  computeConvexHull,
  simplifyPolygon,
  reduceHullVertices,
  extractSpriteOutline,
  generateConvexFromSprite,
  ensureCCW,
  detectOptimalShape,
  generateOptimalCollider,
  type Point2D,
  type DetectedShape,
  type DetectedShapeType,
} from './ConvexHull';

// ── Broad phase ─────────────────────────────────────────────────────────────
export { SpatialHash } from './collision/BroadPhase';

// ── Narrow phase ────────────────────────────────────────────────────────────
export { testCollision } from './collision/NarrowPhase';

// ── Raycasting & overlap queries ────────────────────────────────────────────
export {
  raycast,
  raycastAll,
  overlapPoint,
  overlapBox,
  overlapCircle,
} from './collision/Raycast2D';

// ── Dynamics ────────────────────────────────────────────────────────────────
export { solveVelocities, solvePositions } from './dynamics/Solver';
export { PhysicsWorld, type GravityZoneState } from './dynamics/PhysicsWorld';

// ── Unified engine interface ────────────────────────────────────────────────
export {
  type IPhysicsEngine2D,
  type PhysicsWorldOptions,
  type PhysicsDiagnostics,
  type RigidBodyDescriptor,
  type ColliderDescriptor,
  type ColliderShape,
  type BodyTransform,
  type PhysicsMaterial,
  type RaycastHit,
  type CollisionEventData,
  type TriggerEventData,
  type PhysicsBodyHandle,
  type PhysicsColliderHandle,
  type PhysicsBodyEventHandlers,
  type PhysicsCollisionCallback,
  type PhysicsTriggerCallback,
  type GravityZoneDescriptor,
  type BuoyancyZoneDescriptor,
  type WindZoneDescriptor,
  type SurfaceEffectorDescriptor,
  PhysicsBodyType,
  GravityZoneType as EngineGravityZoneType,
  GravityZoneShape as EngineGravityZoneShape,
  GravityFalloff as EngineGravityFalloff,
  WindZoneMode,
  WindZoneShape,
} from './engine-interface';

// ── Built-in engine adapter ─────────────────────────────────────────────────
export { BuiltinPhysicsEngine } from './BuiltinPhysicsEngine';
